"use client";

import Section from "@/components/common/Section/Section";
import { TextElementNova } from "@/components/ui-kit/TextElementNova/TextElementNova";
import { LearningProvider, useLearning } from "@/context/NutrafolAcademyContext";
import type { NutrafolAcademyData } from "@/types/nutrafolAcademy";

import AdditionalCourses from "./AdditionalCourses";
import CourseCard from "./CourseCard";
import CourseModal from "./CourseModal";
import LearningProgress from "./LearningProgress";

export type LearningData = NutrafolAcademyData["learning"];

export interface LearningProps {
  data: LearningData;
}

const LearningSection = ({ data }: LearningProps) => {
  const { title, description, additionalCourses } = data;
  const { hydrated, courses } = useLearning();

  if (!hydrated) return null;

  return (
    <div className="bg-nova-base-black">
      <Section>
        <div className="flex flex-col lg:flex-row mb-10 md:mb-12 xl:mb-20">
          <div className="w-full lg:w-1/2 mb-4 lg:mb-0 lg:pr-6">
            <TextElementNova text={title} className="Title-20-Super-Normal text-nova-base-white" />
          </div>
          <div className="w-full lg:w-1/2">
            <TextElementNova
              text={description}
              className="Body-30-Dia-Normal text-nova-base-white"
            />
          </div>
        </div>

        <div className="flex flex-col md:flex-row gap-10 md:gap-4 xl:gap-16">
          <div className="w-full md:w-[250px] lg:w-[400px]">
            <div className="md:sticky md:top-[74px] lg:top-[100px]">
              <LearningProgress />
            </div>
          </div>

          <div className="flex-1">
            {courses.map((course, index) => (
              <div
                key={`course-card-${index}`}
                className={index + 1 !== courses.length ? "mb-10" : ""}
              >
                <CourseCard index={index} course={course} />
              </div>
            ))}
            {additionalCourses?.length ? (
              <div className="mt-10">
                <AdditionalCourses courses={additionalCourses} />
              </div>
            ) : null}
          </div>
        </div>
        <CourseModal />
      </Section>
    </div>
  );
};

const Learning = ({ data }: LearningProps) => {
  return (
    <LearningProvider courses={data.courses}>
      <LearningSection data={data} />
    </LearningProvider>
  );
};

export default Learning;
